import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NzImageService } from 'ng-zorro-antd/image';
import { lastValueFrom } from 'rxjs';

import { BaseDownloadServiceWithFilter } from '@cl/core/services/base.service';
import { CommandResult } from '@cl/shared/models';
import { FileMetadata } from '@cl/shared/models/file.model';

import { HrOrder } from '@cl/shared/models/hr-orders/hr-order.model';
import { HrOrderFilter } from '@cl/shared/models/hr-orders/hr-order-filter.model';
import { HrOrderFormConfig } from '@cl/shared/models/hr-orders/hr-order-form-config.model';

@Injectable({
    providedIn: 'root',
})
export class HrOrdersService extends BaseDownloadServiceWithFilter<HrOrder, HrOrderFilter> {
    constructor(
        httpClient: HttpClient,
        protected nzImageService: NzImageService,
    ) {
        super(httpClient, '/api/v1/hr-orders/', nzImageService);
    }

    toString(hrOrder: HrOrder): string {
        const number: string = hrOrder.number || 'Bez numura';

        return hrOrder.subject ? `${number} · ${hrOrder.subject}` : number;
    }

    // Tikai HR-Orders domēna konfigurācija — citu domēnu atlases sauc no to īpašnieku servisiem.
    async getFormConfig(hrOrderId: number): Promise<HrOrderFormConfig> {
        let params = new HttpParams();

        if (hrOrderId) {
            params = params.append('hrOrderId', `${hrOrderId}`);
        }

        const result = await lastValueFrom(this.httpClient.get<CommandResult<HrOrderFormConfig>>(`${this.baseApiUrl}form-config`, { params }));

        return (
            result?.data ?? {
                types: [],
                canChooseCompany: false,
                canSetApprovalRequirements: false,
            }
        );
    }

    async consent(hrOrderId: number): Promise<void> {
        return await lastValueFrom(this.httpClient.patch<void>(`${this.baseApiUrl}consent`, { hrOrderId: hrOrderId }));
    }

    async acknowledge(hrOrderId: number): Promise<void> {
        return await lastValueFrom(this.httpClient.patch<void>(`${this.baseApiUrl}acknowledge`, { hrOrderId: hrOrderId }));
    }

    // eParaksta iepazīšanās: serveris sagatavo (vai atkārtoti izmanto) ŠĪ darbinieka
    // apliecinājuma failu un atgriež tā ID, ar ko sākt parakstīšanu.
    async prepareAcknowledgementSigning(hrOrderId: number): Promise<number> {
        const result = await lastValueFrom(
            this.httpClient.post<CommandResult<number>>(`${this.baseApiUrl}prepare-acknowledgement-signing/${hrOrderId}`, null),
        );

        return result?.data;
    }

    async markCardReceived(hrOrderId: number, hrOrderEmployeeId: number): Promise<void> {
        return await lastValueFrom(
            this.httpClient.patch<void>(`${this.baseApiUrl}card-received`, {
                hrOrderId: hrOrderId,
                hrOrderEmployeeId: hrOrderEmployeeId,
            }),
        );
    }

    async editCostRegistration(hrOrderId: number, isCostRegistrationEnabled: boolean): Promise<void> {
        return await lastValueFrom(
            this.httpClient.patch<void>(`${this.baseApiUrl}cost-registration`, {
                hrOrderId: hrOrderId,
                isCostRegistrationEnabled: isCostRegistrationEnabled,
            }),
        );
    }

    async changeCoordinator(hrOrderId: number, coordinatorPerformerId: number): Promise<void> {
        return await lastValueFrom(
            this.httpClient.patch<void>(`${this.baseApiUrl}coordinator`, {
                hrOrderId: hrOrderId,
                coordinatorPerformerId: coordinatorPerformerId,
            }),
        );
    }

    async assignNumber(hrOrderId: number): Promise<void> {
        return await lastValueFrom(this.httpClient.post<void>(`${this.baseApiUrl}assign-number/${hrOrderId}`, null));
    }

    async changeStatus(hrOrderId: number, action: string, decisionNotes: string = null): Promise<void> {
        return await lastValueFrom(
            this.httpClient.patch<void>(`${this.baseApiUrl}change-status`, {
                hrOrderId: hrOrderId,
                action: action,
                decisionNotes: decisionNotes,
            }),
        );
    }

    async downloadFile(hrOrderId: number, hrOrderFileId: number): Promise<void> {
        const params = new HttpParams().set('hrOrderId', hrOrderId.toString()).set('hrOrderFileId', hrOrderFileId.toString());

        return await this.handleFileDownloading(
            lastValueFrom(
                this.httpClient.get<CommandResult<FileMetadata>>(`${this.baseApiUrl}download-file`, {
                    params,
                }),
            ),
        );
    }

    async getFileMetadata(hrOrderId: number, hrOrderFileId: number): Promise<CommandResult<FileMetadata>> {
        const params = new HttpParams().set('hrOrderId', hrOrderId.toString()).set('hrOrderFileId', hrOrderFileId.toString());

        return await lastValueFrom(this.httpClient.get<CommandResult<FileMetadata>>(`${this.baseApiUrl}download-file`, { params }));
    }

    // Darbinieka iepazīšanās apliecinājums — tikai pa darbinieka rindas tvēruma ceļu.
    async downloadAcknowledgement(hrOrderId: number, hrOrderEmployeeId: number): Promise<void> {
        const params = new HttpParams().set('hrOrderId', hrOrderId.toString()).set('hrOrderEmployeeId', hrOrderEmployeeId.toString());

        return await this.handleFileDownloading(
            lastValueFrom(
                this.httpClient.get<CommandResult<FileMetadata>>(`${this.baseApiUrl}download-acknowledgement`, {
                    params,
                }),
            ),
        );
    }

    async addFile(fileFormData: FormData): Promise<void> {
        return await lastValueFrom(this.httpClient.post<void>(`${this.baseApiUrl}hr-order-file`, fileFormData));
    }

    async deleteFile(hrOrderId: number, hrOrderFileId: number): Promise<void> {
        return await lastValueFrom(this.httpClient.delete<void>(`${this.baseApiUrl}hr-order-file/${hrOrderId}/${hrOrderFileId}`));
    }
}
